import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, openSignedFile } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { SecureImage } from "@/components/SecureImage";
import { StatusBadge } from "@/components/StatusBadge";
import { Loader, EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ShieldCheck, Store, Truck, Clock, CheckCircle2, XCircle, IndianRupee, LogOut,
  LayoutDashboard, ClipboardList, CreditCard, Activity, FileText, Search, Power, Menu, X,
  Factory, Warehouse, Inbox,
} from "lucide-react";
import { AdminEnquiries } from "@/components/AdminEnquiries";
import { AdminPlans } from "@/components/AdminPlans";
import { BUSINESS_TYPE_LABELS } from "@/lib/constants";

const SECTIONS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "approvals", label: "Approvals", icon: ClipboardList },
  { id: "accounts", label: "Accounts", icon: Store },
  { id: "subscriptions", label: "Subscriptions", icon: CreditCard },
  { id: "activity", label: "Activity", icon: Activity },
  { id: "enquiries", label: "Enquiries", icon: Inbox },
  { id: "plans", label: "Plans & Pricing", icon: CreditCard },
];

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("overview");
  const [navOpts, setNavOpts] = useState({});
  const [menuOpen, setMenuOpen] = useState(false);
  const go = (s, opts = {}) => { setNavOpts(opts); setSection(s); setMenuOpen(false); };

  const SidebarInner = (
    <div className="flex h-full w-64 flex-col border-r border-white/10 bg-[#0b1322]">
      <div className="flex items-center gap-2 px-6 py-5">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#0D5C3A]"><ShieldCheck className="h-5 w-5" /></div>
        <span className="font-heading text-lg font-extrabold">Admin</span>
      </div>
      <nav className="flex-1 space-y-1 px-3">
        {SECTIONS.map((s) => (
          <button key={s.id} data-testid={`admin-nav-${s.id}`} onClick={() => go(s.id)}
            className={`flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${section === s.id ? "bg-[#0D5C3A] text-white" : "text-stone-400 hover:bg-white/5"}`}>
            <s.icon className="h-5 w-5" /> {s.label}
          </button>
        ))}
      </nav>
      <button data-testid="admin-signout-btn" onClick={async () => { await logout(); navigate("/"); }} className="m-3 mb-24 flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm text-stone-400 hover:bg-white/5">
        <LogOut className="h-5 w-5" /> Sign Out
      </button>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-[#0f1a2b] text-stone-100">
      <div className="hidden lg:block">{SidebarInner}</div>
      {menuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="admin-mobile-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMenuOpen(false)} />
          <div className="absolute left-0 top-0 h-full">
            <div className="relative h-full">
              {SidebarInner}
              <button onClick={() => setMenuOpen(false)} className="absolute right-3 top-5 text-stone-400" data-testid="admin-drawer-close"><X className="h-5 w-5" /></button>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1">
        <header className="flex items-center justify-between border-b border-white/10 px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setMenuOpen(true)} data-testid="admin-mobile-menu-btn"><Menu className="h-6 w-6" /></button>
            <h1 className="font-heading text-xl font-bold capitalize">{section}</h1>
          </div>
          <button data-testid="admin-topbar-signout-btn" onClick={async () => { await logout(); navigate("/"); }} className="inline-flex items-center gap-1 rounded-lg bg-white/5 px-3 py-2 text-sm font-medium text-stone-200 hover:bg-white/10 lg:hidden">
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
        </header>
        <main className="rv-scroll max-h-[calc(100vh-65px)] overflow-y-auto p-6">
          {section === "overview" && <Overview go={go} />}
          {section === "approvals" && <Approvals initialQueue={navOpts.queue} />}
          {section === "accounts" && <Accounts initialFilter={navOpts} />}
          {section === "subscriptions" && <Subscriptions />}
          {section === "activity" && <ActivityLog />}
          {section === "enquiries" && <AdminEnquiries />}
          {section === "plans" && <AdminPlans />}
        </main>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      data-testid={`admin-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className={`block w-full rounded-2xl border border-white/10 bg-[#0b1322] p-5 text-left transition-all ${onClick ? "cursor-pointer hover:border-emerald-500/40 hover:bg-[#101d31]" : "cursor-default"}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-stone-400">{label}</span>
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <p className="mt-3 font-heading text-3xl font-extrabold">{value}</p>
    </button>
  );
}

function Overview({ go }) {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return <Loader label="Loading platform stats…" />;
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Factory} label="Manufacturers" value={stats.total_manufacturers} color="#34d399" onClick={() => go("accounts", { type: "manufacturer", status: "approved" })} />
        <Stat icon={Truck} label="Distributors" value={stats.total_distributors} color="#fbbf24" onClick={() => go("accounts", { type: "distributor", status: "approved" })} />
        <Stat icon={Warehouse} label="Wholesalers" value={stats.total_wholesalers} color="#60a5fa" onClick={() => go("accounts", { type: "wholesaler", status: "approved" })} />
        <Stat icon={Store} label="Retailers" value={stats.total_retailers} color="#c084fc" onClick={() => go("accounts", { type: "retailer", status: "approved" })} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Clock} label="Pending Requests" value={stats.pending_requests} color="#f59e0b" onClick={() => go("approvals", { queue: "pending" })} />
        <Stat icon={CheckCircle2} label="Approved" value={stats.approved_accounts} color="#34d399" onClick={() => go("accounts", { status: "approved" })} />
        <Stat icon={XCircle} label="Rejected" value={stats.rejected_accounts} color="#fb7185" onClick={() => go("accounts", { status: "rejected" })} />
        <Stat icon={Power} label="Active" value={stats.active_accounts} color="#34d399" onClick={() => go("accounts", { status: "approved" })} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={IndianRupee} label="Revenue" value={`₹${stats.revenue}`} color="#34d399" onClick={() => go("subscriptions")} />
        <Stat icon={CreditCard} label="Active Subs" value={stats.active_subscriptions} color="#34d399" onClick={() => go("subscriptions")} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-white/10 bg-[#0b1322] p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-lg font-semibold">Recent Registrations</h2>
            <button className="text-sm text-emerald-400 hover:underline" onClick={() => go("approvals")}>View all</button>
          </div>
          {stats.recent_registrations.length === 0 ? <p className="text-sm text-stone-500">None yet.</p> : (
            <div className="space-y-3">
              {stats.recent_registrations.map((b) => (
                <div key={b.id} className="flex items-center justify-between">
                  <div><p className="text-sm font-semibold">{b.name}</p><p className="font-mono text-xs text-stone-400">{b.profile_code}</p></div>
                  <StatusBadge status={b.registration_status} />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="rounded-2xl border border-white/10 bg-[#0b1322] p-6">
          <h2 className="mb-4 font-heading text-lg font-semibold">Recent Activity</h2>
          <div className="space-y-2">
            {stats.recent_activity.slice(0, 8).map((a) => (
              <div key={a.id} className="flex items-start gap-2 text-sm text-stone-300">
                <Activity className="mt-0.5 h-4 w-4 text-stone-500" />
                <span><b className="capitalize">{a.action.replace(/_/g, " ")}</b> {a.target && `· ${a.target}`}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function BusinessDetailDialog({ bizId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  useEffect(() => { if (bizId) api.get(`/admin/businesses/${bizId}`).then((r) => setData(r.data)); else setData(null); }, [bizId]);
  if (!bizId) return null;
  const b = data?.business;
  return (
    <Dialog open={!!bizId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto bg-[#0b1322] text-stone-100" data-testid="admin-business-detail">
        <DialogHeader><DialogTitle>{b?.name || "Loading…"}</DialogTitle></DialogHeader>
        {!data ? <Loader /> : (
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-3">
              <SecureImage fileId={b.logo_file_id} className="h-14 w-14 rounded-xl object-cover" />
              <div>
                <p className="font-mono text-xs text-emerald-400">{b.profile_code}</p>
                <span className="capitalize text-stone-400">{BUSINESS_TYPE_LABELS[b.business_type] || b.business_type}</span>
                <div className="mt-1 flex gap-2"><StatusBadge status={b.registration_status} /><StatusBadge status={b.subscription_status} /></div>
              </div>
            </div>
            {[["Owner", b.owner_name], ["Mobile", b.owner_mobile], ["Address", b.address], ["License Type", b.license_type],
              ["License No.", b.license_number], ["Issue Date", b.license_issue_date], ["Expiry Date", b.license_expiry_date]].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 border-b border-white/5 py-1.5"><span className="text-stone-400">{k}</span><span className="text-right">{v}</span></div>
            ))}
            {b.license_doc_file_id && (
              <button type="button" onClick={() => openSignedFile(b.license_doc_file_id).catch(() => toast.error("Could not open document"))}
                className="inline-flex items-center gap-2 rounded-lg border border-white/10 px-4 py-2 text-emerald-400 hover:bg-white/5" data-testid="admin-view-license">
                <FileText className="h-4 w-4" /> View License Document
              </button>
            )}
            {b.rejection_reason && <p className="rounded-lg bg-rose-500/10 px-3 py-2 text-rose-300">Rejected: {b.rejection_reason}</p>}

            {data.subscriptions?.[0] && (
              <div className="rounded-lg border border-white/10 p-3" data-testid="admin-detail-subscription">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-stone-400">Subscription</p>
                <div className="flex items-center justify-between">
                  <span>{data.subscriptions[0].plan_name} · ₹{data.subscriptions[0].amount}</span>
                  <StatusBadge status={data.subscriptions[0].payment_status} />
                </div>
                {data.subscriptions[0].expiry_date && <p className="mt-1 text-xs text-stone-500">Expires {new Date(data.subscriptions[0].expiry_date).toLocaleDateString()}</p>}
              </div>
            )}

            <div className="rounded-lg border border-white/10 p-3" data-testid="admin-detail-orders">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-stone-400">Orders ({data.orders?.length || 0})</p>
              {(data.orders?.length || 0) === 0 ? <p className="text-xs text-stone-500">No orders yet.</p> : (
                <div className="max-h-40 space-y-1.5 overflow-y-auto">
                  {data.orders.slice(0, 20).map((o) => (
                    <div key={o.id} className="flex items-center justify-between gap-2">
                      <span className="font-mono text-xs text-stone-300">{o.order_code} <span className="text-stone-500">· {o.direction === "outgoing" ? "→ " + o.to_name : "← " + o.from_name}</span></span>
                      <StatusBadge status={o.status} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-lg border border-white/10 p-3" data-testid="admin-detail-partners">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-stone-400">Partners ({data.partners?.length || 0})</p>
              {(data.partners?.length || 0) === 0 ? <p className="text-xs text-stone-500">No partners connected.</p> : (
                <div className="space-y-1.5">
                  {data.partners.map((p) => (
                    <div key={p.id} className="flex items-center justify-between">
                      <span className="text-sm">{p.name} <span className="capitalize text-stone-500">· {BUSINESS_TYPE_LABELS[p.business_type] || p.business_type}</span></span>
                      <span className="font-mono text-xs text-stone-400">{p.profile_code}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const REJECT_FIELDS = [
  ["name", "Business Name"], ["owner_name", "Owner Name"], ["address", "Address"],
  ["business_email", "Email"], ["logo_file_id", "Logo"], ["license_type", "License Type"],
  ["license_number", "License Number"], ["license_issue_date", "Issue Date"],
  ["license_expiry_date", "Expiry Date"], ["license_doc_file_id", "License Document"],
];

function Approvals({ initialQueue }) {
  const [items, setItems] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reject, setReject] = useState(null);
  const [reason, setReason] = useState("");
  const [flags, setFlags] = useState([]);
  const [queue, setQueue] = useState(initialQueue || "pending"); // pending | resubmitted
  const load = (q = queue) => api.get(`/admin/businesses?status=${q}`).then((r) => setItems(r.data.businesses));
  useEffect(() => { load(queue); }, [queue]); // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async (id) => { await api.post(`/admin/businesses/${id}/approve`); toast.success("Approved"); load(); };
  const openReject = (id) => { setReject(id); setReason(""); setFlags([]); };
  const toggleFlag = (k) => setFlags((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const doReject = async () => {
    if (!reason.trim()) { toast.error("Provide a reason"); return; }
    await api.post(`/admin/businesses/${reject}/reject`, { reason, rejected_fields: flags });
    toast.success("Rejected"); setReject(null); setReason(""); setFlags([]); load();
  };

  return (
    <div className="space-y-3" data-testid="admin-approvals">
      <div className="flex gap-2">
        {[["pending", "All Pending"], ["resubmitted", "Resubmitted"]].map(([q, label]) => (
          <button key={q} onClick={() => setQueue(q)} data-testid={`admin-queue-${q}`}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${queue === q ? "bg-emerald-600 text-white" : "bg-white/5 text-stone-300 hover:bg-white/10"}`}>
            {label}
          </button>
        ))}
      </div>
      {!items ? <Loader /> : items.length === 0 ? (
        <EmptyState icon={CheckCircle2} title={queue === "resubmitted" ? "No resubmitted profiles" : "No pending requests"} description="All caught up — nothing awaiting review." testid="admin-approvals-empty" />
      ) : items.map((b) => (
        <div key={b.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-[#0b1322] p-4">
          <div className="flex items-center gap-3">
            <SecureImage fileId={b.logo_file_id} className="h-12 w-12 rounded-xl object-cover" />
            <div>
              <p className="font-semibold">{b.name}
                {b.resubmitted && <span className="ml-2 rounded-full bg-amber-500/20 px-2 py-0.5 text-xs font-semibold text-amber-300" data-testid={`admin-resubmitted-badge-${b.id}`}>Resubmitted</span>}
              </p>
              <p className="font-mono text-xs text-stone-400">{b.profile_code} · {BUSINESS_TYPE_LABELS[b.business_type] || b.business_type}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="border-white/20 bg-transparent text-stone-200 hover:bg-white/10" onClick={() => setDetail(b.id)} data-testid={`admin-review-${b.id}`}>Review</Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => approve(b.id)} data-testid={`admin-approve-${b.id}`}>Approve</Button>
            <Button size="sm" variant="outline" className="border-rose-500/50 bg-transparent text-rose-300 hover:bg-rose-500/10" onClick={() => openReject(b.id)} data-testid={`admin-reject-${b.id}`}>Reject</Button>
          </div>
        </div>
      ))}
      <BusinessDetailDialog bizId={detail} onClose={() => setDetail(null)} onChanged={load} />
      <Dialog open={!!reject} onOpenChange={(o) => !o && setReject(null)}>
        <DialogContent className="bg-[#0b1322] text-stone-100" data-testid="admin-reject-dialog">
          <DialogHeader><DialogTitle>Reject registration</DialogTitle></DialogHeader>
          <Label>Rejection reason *</Label>
          <Textarea data-testid="admin-reject-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="bg-white/5" placeholder="e.g. License document unclear" />
          <Label className="mt-2">Fields to correct (optional — the user can edit only these)</Label>
          <div className="grid grid-cols-2 gap-2">
            {REJECT_FIELDS.map(([k, label]) => (
              <label key={k} className="flex cursor-pointer items-center gap-2 text-sm text-stone-300" data-testid={`admin-flag-${k}`}>
                <input type="checkbox" checked={flags.includes(k)} onChange={() => toggleFlag(k)} className="accent-emerald-600" />
                {label}
              </label>
            ))}
          </div>
          <DialogFooter><Button variant="outline" className="bg-transparent" onClick={() => setReject(null)}>Cancel</Button><Button className="bg-rose-600 hover:bg-rose-700" onClick={doReject} data-testid="admin-reject-confirm">Reject</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Accounts({ initialFilter = {} }) {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [type, setType] = useState(initialFilter.type || "");
  const [status, setStatus] = useState(initialFilter.status || "approved");
  const [detail, setDetail] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [reject, setReject] = useState(null);
  const [reason, setReason] = useState("");
  const [flags, setFlags] = useState([]);
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PER_PAGE = 20;

  const load = () => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (type) params.set("business_type", type);
    if (q) params.set("q", q);
    params.set("page", page);
    params.set("per_page", PER_PAGE);
    return api.get(`/admin/businesses?${params}`).then((r) => {
      const list = [...r.data.businesses].sort((a, b) => (b.resubmitted ? 1 : 0) - (a.resubmitted ? 1 : 0));
      setItems(list); setSelected([]); setTotal(r.data.total ?? list.length);
    });
  };
  useEffect(() => { load(); }, [type, status, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSel = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const bulkApprove = async () => {
    await Promise.all(selected.map((id) => api.post(`/admin/businesses/${id}/approve`)));
    toast.success(`Approved ${selected.length} account(s)`); setSelected([]); load();
  };
  const bulkDecline = () => { setReject({ bulk: true, name: `${selected.length} account(s)` }); setReason(""); setFlags([]); };

  const toggle = async (b) => {
    if (b.account_active) setConfirm(b);
    else { await api.post(`/admin/businesses/${b.id}/reactivate`); toast.success("Reactivated"); load(); }
  };
  const doDeactivate = async (permanent) => { await api.post(`/admin/businesses/${confirm.id}/deactivate`, { permanent }); toast.success("Deactivated"); setConfirm(null); load(); };

  const approve = async (b) => { await api.post(`/admin/businesses/${b.id}/approve`); toast.success(`${b.name} approved — prompted to proceed with payment`); load(); };
  const openReject = (b) => { setReject(b); setReason(""); setFlags([]); };
  const toggleFlag = (k) => setFlags((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const doReject = async () => {
    if (!reason.trim()) { toast.error("Provide a rejection reason"); return; }
    const ids = reject?.bulk ? selected : [reject.id];
    await Promise.all(ids.map((id) => api.post(`/admin/businesses/${id}/reject`, { reason, rejected_fields: flags })));
    toast.success("Declined — the account(s) can edit and resubmit"); setReject(null); setReason(""); setFlags([]); setSelected([]); load();
  };

  if (!items) return <Loader />;

  const pendingRows = items.filter((b) => b.registration_status !== "approved");
  const allSelected = pendingRows.length > 0 && selected.length === pendingRows.length;
  const toggleSelAll = () => setSelected(allSelected ? [] : pendingRows.map((b) => b.id));

  return (
    <div className="space-y-4" data-testid="admin-accounts">
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-stone-500" />
          <Input data-testid="admin-account-search" className="bg-white/5 pl-9" placeholder="Search name or code…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} />
        </div>
        {["", "manufacturer", "distributor", "wholesaler", "retailer"].map((t) => (
          <button key={t || "all"} onClick={() => { setType(t); setPage(1); }} data-testid={`admin-filter-${t || "all"}`}
            className={`rounded-lg border px-4 text-sm font-medium ${type === t ? "border-emerald-500 bg-emerald-600/20 text-emerald-300" : "border-white/10 text-stone-400"}`}>{t ? BUSINESS_TYPE_LABELS[t] : "All"}</button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {[["approved", "Approved"], ["pending", "Pending"], ["rejected", "Rejected"], ["", "All Statuses"]].map(([s, label]) => (
          <button key={s || "all"} onClick={() => { setStatus(s); setPage(1); }} data-testid={`admin-status-${s || "all"}`}
            className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${status === s ? "bg-emerald-600 text-white" : "bg-white/5 text-stone-400 hover:bg-white/10"}`}>{label}</button>
        ))}
      </div>
      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-4 py-3" data-testid="admin-bulk-bar">
          <span className="text-sm text-stone-300">{selected.length} selected</span>
          <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={bulkApprove} data-testid="admin-bulk-approve">Approve selected</Button>
          <Button size="sm" variant="outline" className="border-rose-500/50 bg-transparent text-rose-300 hover:bg-rose-500/10" onClick={bulkDecline} data-testid="admin-bulk-decline">Decline selected</Button>
          <button className="text-xs text-stone-400 hover:underline" onClick={() => setSelected([])}>Clear</button>
        </div>
      )}
      {items.length === 0 ? <EmptyState icon={Store} title="No accounts" testid="admin-accounts-empty" /> : (
        <div className="overflow-hidden rounded-2xl border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-left text-xs uppercase text-stone-400"><tr>
              <th className="w-10 px-5 py-3">{pendingRows.length > 0 && <input type="checkbox" checked={allSelected} onChange={toggleSelAll} className="accent-emerald-600" data-testid="admin-bulk-select-all" />}</th>
              <th className="px-5 py-3">Business</th><th className="px-5 py-3">Code</th><th className="hidden px-5 py-3 sm:table-cell">Subscription</th><th className="px-5 py-3">Active</th><th className="px-5 py-3"></th></tr></thead>
            <tbody className="divide-y divide-white/5">
              {items.map((b) => (
                <tr key={b.id} data-testid={`admin-account-${b.profile_code}`}>
                  <td className="px-5 py-3">{b.registration_status !== "approved" && <input type="checkbox" checked={selected.includes(b.id)} onChange={() => toggleSel(b.id)} className="accent-emerald-600" data-testid={`admin-bulk-check-${b.profile_code}`} />}</td>
                  <td className="px-5 py-3">
                    <span className="font-semibold">{b.name}</span> <span className="capitalize text-stone-500">· {BUSINESS_TYPE_LABELS[b.business_type] || b.business_type}</span>
                    {b.resubmitted && <span className="ml-2 rounded-full bg-amber-500/20 px-2 py-0.5 text-xs font-semibold text-amber-300" data-testid={`admin-account-resubmitted-${b.profile_code}`}>Resubmitted</span>}
                  </td>
                  <td className="px-5 py-3 font-mono text-xs text-stone-400">{b.profile_code}</td>
                  <td className="hidden px-5 py-3 sm:table-cell"><StatusBadge status={b.subscription_status} /></td>
                  <td className="px-5 py-3">{b.account_active ? <span className="text-emerald-400">Active</span> : <span className="text-rose-400">Inactive</span>}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      {b.registration_status === "approved" ? (
                        <>
                          <Button size="sm" variant="outline" className="border-white/20 bg-transparent text-stone-200 hover:bg-white/10" onClick={() => setDetail(b.id)} data-testid={`admin-account-view-${b.profile_code}`}>View Details</Button>
                          <Button size="sm" variant="outline" className={`bg-transparent ${b.account_active ? "border-rose-500/50 text-rose-300" : "border-emerald-500/50 text-emerald-300"}`} onClick={() => toggle(b)} data-testid={`admin-account-toggle-${b.profile_code}`}>
                            {b.account_active ? "Deactivate" : "Reactivate"}
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => approve(b)} data-testid={`admin-account-approve-${b.profile_code}`}>Approve</Button>
                          <Button size="sm" variant="outline" className="border-rose-500/50 bg-transparent text-rose-300 hover:bg-rose-500/10" onClick={() => openReject(b)} data-testid={`admin-account-decline-${b.profile_code}`}>Decline</Button>
                          <Button size="sm" variant="ghost" className="text-stone-300 hover:bg-white/10" onClick={() => setDetail(b.id)} data-testid={`admin-account-view-${b.profile_code}`}>View Details</Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {total > PER_PAGE && (
        <div className="flex items-center justify-between" data-testid="admin-accounts-pagination">
          <span className="text-sm text-stone-400">Page {page} of {Math.ceil(total / PER_PAGE)} · {total} total</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="bg-transparent" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="admin-accounts-prev">Prev</Button>
            <Button variant="outline" size="sm" className="bg-transparent" disabled={page >= Math.ceil(total / PER_PAGE)} onClick={() => setPage((p) => p + 1)} data-testid="admin-accounts-next">Next</Button>
          </div>
        </div>
      )}
      <BusinessDetailDialog bizId={detail} onClose={() => setDetail(null)} onChanged={load} />
      <Dialog open={!!reject} onOpenChange={(o) => !o && setReject(null)}>
        <DialogContent className="bg-[#0b1322] text-stone-100" data-testid="admin-account-decline-dialog">
          <DialogHeader><DialogTitle>Decline {reject?.name}</DialogTitle></DialogHeader>
          <Label>Rejection reason *</Label>
          <Textarea data-testid="admin-account-decline-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="bg-white/5" placeholder="e.g. License document unclear" />
          <Label className="mt-2">Fields to correct (optional — the user can edit only these)</Label>
          <div className="grid grid-cols-2 gap-2">
            {REJECT_FIELDS.map(([k, label]) => (
              <label key={k} className="flex cursor-pointer items-center gap-2 text-sm text-stone-300" data-testid={`admin-account-flag-${k}`}>
                <input type="checkbox" checked={flags.includes(k)} onChange={() => toggleFlag(k)} className="accent-emerald-600" />
                {label}
              </label>
            ))}
          </div>
          <DialogFooter><Button variant="outline" className="bg-transparent" onClick={() => setReject(null)}>Cancel</Button><Button className="bg-rose-600 hover:bg-rose-700" onClick={doReject} data-testid="admin-account-decline-confirm">Decline</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-[#0b1322] text-stone-100">
          <AlertDialogHeader><AlertDialogTitle>Deactivate {confirm?.name}?</AlertDialogTitle><AlertDialogDescription className="text-stone-400">Temporary deactivation can be reversed. Permanent deactivation should be used carefully. Order history is preserved either way.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent">Cancel</AlertDialogCancel>
            <Button variant="outline" className="border-amber-500/50 bg-transparent text-amber-300" onClick={() => doDeactivate(false)} data-testid="admin-deactivate-temp">Temporary</Button>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => doDeactivate(true)} data-testid="admin-deactivate-perm">Permanent</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Subscriptions() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/subscriptions").then((r) => setItems(r.data.subscriptions)); }, []);
  if (!items) return <Loader />;
  if (items.length === 0) return <EmptyState icon={CreditCard} title="No subscriptions yet" testid="admin-subs-empty" />;
  return (
    <div className="overflow-hidden rounded-2xl border border-white/10" data-testid="admin-subscriptions">
      <table className="w-full text-sm">
        <thead className="bg-white/5 text-left text-xs uppercase text-stone-400"><tr><th className="px-5 py-3">Business</th><th className="px-5 py-3">Plan</th><th className="px-5 py-3">Amount</th><th className="px-5 py-3">Status</th><th className="hidden px-5 py-3 sm:table-cell">Expiry</th></tr></thead>
        <tbody className="divide-y divide-white/5">
          {items.map((s) => (
            <tr key={s.id}>
              <td className="px-5 py-3"><span className="font-semibold">{s.business_name}</span><br /><span className="font-mono text-xs text-stone-400">{s.profile_code}</span></td>
              <td className="px-5 py-3">{s.plan_name}</td>
              <td className="px-5 py-3">₹{s.amount}</td>
              <td className="px-5 py-3"><StatusBadge status={s.payment_status} /></td>
              <td className="hidden px-5 py-3 text-stone-400 sm:table-cell">{s.expiry_date ? new Date(s.expiry_date).toLocaleDateString() : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActivityLog() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/activity").then((r) => setItems(r.data.activity)); }, []);
  if (!items) return <Loader />;
  return (
    <div className="space-y-2" data-testid="admin-activity">
      {items.map((a) => (
        <div key={a.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-[#0b1322] px-4 py-3 text-sm">
          <span className="flex items-center gap-2"><Activity className="h-4 w-4 text-stone-500" /><b className="capitalize">{a.action.replace(/_/g, " ")}</b> {a.target && <span className="text-stone-400">· {a.target}</span>}</span>
          <span className="text-xs text-stone-500">{new Date(a.created_at).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}
